import {
  ArrowDownToLine,
  Ban,
  FileSpreadsheet,
  FolderOpen,
  LockKeyhole,
  Play,
  ShieldCheck,
} from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';

import { Button } from '../../components/Button';
import { MAX_FILE_SIZE_LABEL } from './validation';

export interface UploadScreenProps {
  readonly busy: boolean;
  readonly demoBusy: boolean;
  readonly error: string | null;
  readonly onSelectFile: (file: File) => void;
  readonly onLoadDemo: () => void;
  readonly onCancel: () => void;
}

export function UploadScreen({
  busy,
  demoBusy,
  error,
  onSelectFile,
  onLoadDemo,
  onCancel,
}: UploadScreenProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files.item(0);
    if (file !== null && !busy) onSelectFile(file);
  };

  const demoUrl = `${import.meta.env.BASE_URL}demo/boq-check-demo.xlsx`;

  return (
    <main className="bg-slate-100">
      <section className="mx-auto grid min-h-[calc(100dvh-4rem)] max-w-[1440px] items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,0.82fr)_minmax(560px,1.18fr)] lg:px-8 lg:py-14">
        <div className="max-w-xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm">
            <LockKeyhole aria-hidden="true" className="size-4 text-emerald-700" />
            隐私优先的本地 Excel 检查
          </div>
          <h1
            aria-label="上传清单，三分钟完成交付前体检。"
            className="text-4xl leading-[1.08] font-black tracking-[-0.04em] text-slate-900 sm:text-5xl lg:text-[3.55rem]"
          >
            <span className="block">上传清单，</span>
            <span className="block whitespace-nowrap">三分钟完成</span>
            <span className="block whitespace-nowrap">交付前体检。</span>
          </h1>
          <p className="mt-6 max-w-[52ch] text-base leading-7 text-slate-600 sm:text-lg">
            识别工作表和字段映射，检查结构、完整性、文本一致性与金额逻辑，并导出可追溯的问题台账。
          </p>

          <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-slate-300 pt-6 text-sm">
            <div>
              <dt className="font-bold text-slate-900">处理位置</dt>
              <dd className="mt-1 text-slate-600">仅在当前浏览器内存</dd>
            </div>
            <div>
              <dt className="font-bold text-slate-900">支持格式</dt>
              <dd className="mt-1 text-slate-600">.xlsx，最大 {MAX_FILE_SIZE_LABEL}</dd>
            </div>
            <div>
              <dt className="font-bold text-slate-900">检查范围</dt>
              <dd className="mt-1 text-slate-600">结构、文本与金额逻辑</dd>
            </div>
            <div>
              <dt className="font-bold text-slate-900">输出</dt>
              <dd className="mt-1 text-slate-600">XLSX 台账与 JSON 报告</dd>
            </div>
          </dl>
        </div>

        <div className="min-w-0 rounded-xl border border-slate-300 bg-white p-4 shadow-[0_24px_70px_rgba(31,52,72,0.12)] sm:p-6">
          <div
            role="group"
            aria-disabled={busy}
            aria-label="拖拽或选择 Excel 文件"
            onDragEnter={(event) => {
              event.preventDefault();
              if (!busy) setDragActive(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={`grid min-h-72 place-items-center rounded-lg border-2 border-dashed p-6 text-center transition ${
              busy ? 'opacity-70' : ''
            } ${
              dragActive
                ? 'border-orange-600 bg-orange-50'
                : 'border-slate-300 bg-slate-50 hover:border-slate-500 hover:bg-white'
            }`}
          >
            <div>
              <span className="mx-auto grid size-14 place-items-center rounded-lg bg-slate-800 text-white">
                <FileSpreadsheet aria-hidden="true" className="size-7" strokeWidth={1.7} />
              </span>
              <h2 className="mt-5 text-xl font-bold text-slate-900">拖拽清单到这里</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">或从设备中选择一个 .xlsx 文件</p>
              <Button
                className="mt-5"
                variant="primary"
                loading={busy && !demoBusy}
                disabled={busy}
                icon={<FolderOpen aria-hidden="true" className="size-4" />}
                onClick={(event) => {
                  event.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                选择文件
              </Button>
            </div>
          </div>
          <input
            ref={inputRef}
            className="sr-only"
            type="file"
            disabled={busy}
            tabIndex={-1}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              const file = event.currentTarget.files?.item(0);
              if (file !== null && file !== undefined) onSelectFile(file);
              event.currentTarget.value = '';
            }}
          />

          {error !== null && (
            <div
              role="alert"
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800"
            >
              {error}
            </div>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Button
              icon={<Play aria-hidden="true" className="size-4" />}
              loading={demoBusy}
              disabled={busy}
              onClick={onLoadDemo}
            >
              加载演示清单
            </Button>
            <a
              href={demoUrl}
              download="boq-check-demo.xlsx"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold whitespace-nowrap text-slate-800 transition outline-none hover:border-slate-400 hover:bg-slate-50 active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600"
            >
              <ArrowDownToLine aria-hidden="true" className="size-4" />
              下载示例 XLSX
            </a>
          </div>
          {busy && (
            <Button
              className="mt-3 w-full"
              variant="ghost"
              icon={<Ban aria-hidden="true" className="size-4" />}
              onClick={onCancel}
            >
              取消读取
            </Button>
          )}

          <div className="mt-5 flex items-start gap-3 border-t border-slate-200 pt-5 text-xs leading-5 text-slate-600">
            <ShieldCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-emerald-700" />
            <p>
              不写入 localStorage 或 IndexedDB，不连接后端。刷新页面后，当前文件和检查结果会被清除。
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-6 px-4 py-8 text-sm leading-6 text-slate-600 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-bold text-slate-900">适用范围</h2>
            <p className="mt-2">
              用于计价软件或 Excel
              导出后的交付前辅助复核，不修改原文件，不提供工程量重算、定额套用或材料价格审核。
            </p>
          </div>
          <div>
            <h2 className="font-bold text-slate-900">标准与责任边界</h2>
            <p className="mt-2">
              参考工程量清单常见字段和复核流程，不代表通过任何标准认证，也不自动判断清单是否完全符合国家标准。
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
